'use client';

import { DatePicker, MenuItem } from '@lulalife/design-system';
import { useState } from 'react';

type DateRangePresetsProps = {
  presets: ReadonlyArray<{ value: string; label: string }>;
  /** The selected preset's value, or null. */
  selected: string | null;
  /** The filter is a custom range: Custom Range is selected and its dates are open. */
  isCustomRange: boolean;
  customStart: Date | null;
  customEnd: Date | null;
  onSelect: (value: string) => void;
  /** Called once both dates are picked, start on or before end. */
  onCustomRange: (start: Date, end: Date) => void;
};

/**
 * DS Date Picker, Type=Presets (Service Requests 8228:5969): a list of ranges and "Custom Range",
 * which opens start and end dates.
 *
 * DS-GAP(date-picker-presets): the package's `DatePicker` has no presets type, so the list is DS
 * `MenuItem`s and the custom range two single DS `DatePicker`s.
 */
export const DateRangePresets = ({
  presets,
  selected,
  isCustomRange,
  customStart,
  customEnd,
  onSelect,
  onCustomRange,
}: DateRangePresetsProps) => {
  const [isCustomOpen, setIsCustomOpen] = useState(isCustomRange);
  const [start, setStart] = useState<Date | null>(customStart);
  const [end, setEnd] = useState<Date | null>(customEnd);

  const pick = (nextStart: Date | null, nextEnd: Date | null) => {
    setStart(nextStart);
    setEnd(nextEnd);
    if (nextStart && nextEnd && nextStart <= nextEnd) onCustomRange(nextStart, nextEnd);
  };

  return (
    <div className="flex flex-col" data-testid="date-range-presets">
      {presets.map((preset) => (
        <MenuItem
          key={preset.value}
          label={preset.label}
          selected={!isCustomOpen && selected === preset.value}
          onClick={() => {
            setIsCustomOpen(false);
            onSelect(preset.value);
          }}
        />
      ))}
      <MenuItem
        label="Custom Range"
        selected={isCustomOpen}
        onClick={() => setIsCustomOpen(true)}
      />
      {isCustomOpen ? (
        <div className="flex flex-col gap-space-8 px-space-8 pt-space-4">
          <DatePicker
            aria-label="Start date"
            placeholder="Start date"
            value={start}
            maxDate={end ?? undefined}
            onChange={(value) => pick(value instanceof Date ? value : null, end)}
          />
          <DatePicker
            aria-label="End date"
            placeholder="End date"
            value={end}
            minDate={start ?? undefined}
            onChange={(value) => pick(start, value instanceof Date ? value : null)}
          />
        </div>
      ) : null}
    </div>
  );
};
